import { Award, Users, BookOpen, Target, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { BreadcrumbSchema } from '../components/StructuredData';

const AboutPage = () => {
  const values = [
    { icon: Target, title: 'Results-Driven', description: 'We focus on achieving outstanding examination results through structured learning.' },
    { icon: Award, title: 'Academic Excellence', description: 'Committed to the highest standards of education and student achievement.' },
    { icon: Users, title: 'Personalized Learning', description: 'Small batch sizes ensure individual attention for every student.' },
    { icon: BookOpen, title: 'Comprehensive Curriculum', description: 'Complete coverage of Cambridge and Edexcel syllabi with exam-focused strategies.' },
  ];

  const milestones = [
    { year: '2015', title: 'Foundation', description: 'Aspire Academy established with a mission to provide quality international education.' },
    { year: '2017', title: 'Expansion', description: 'Extended programs to include full A Level curriculum across all major subjects.' },
    { year: '2020', title: 'Digital Transformation', description: 'Launched online learning platform for seamless hybrid education.' },
    { year: '2024', title: 'Excellence Continues', description: 'Celebrating 95%+ A*/A achievement rate across all programs.' },
  ];

  return (
    <main data-testid="about-page">
      <SEO
        title="About Us"
        description="Learn about Aspire Academy, Sri Lanka's leading IGCSE and A Level tuition center in Colombo 7. Our mission, values, and 8+ years of academic excellence."
        path="/about"
      />
      <BreadcrumbSchema items={[{ name: 'Home', href: '/' }, { name: 'About' }]} />
      {/* Hero Section */}
      <section className="bg-surface section-spacing">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <span className="label-uppercase text-aspire-red">About Us</span>
              <h1 className="heading-hero mt-2 mb-6">
                Shaping Future <span className="text-aspire-red">Leaders</span> Through Education
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Aspire Academy is Sri Lanka's premier institution for Cambridge and Edexcel 
                IGCSE and A Level tuition. Since 2015, we have been dedicated to helping students 
                achieve academic excellence through rigorous, results-focused education.
              </p>
              <div className="flex flex-wrap gap-4 sm:gap-8">
                <div>
                  <div className="font-heading font-bold text-3xl sm:text-4xl text-aspire-red">500+</div>
                  <div className="text-sm text-gray-500">Students Enrolled</div>
                </div>
                <div>
                  <div className="font-heading font-bold text-3xl sm:text-4xl text-aspire-red">95%</div>
                  <div className="text-sm text-gray-500">A*/A Achievement</div>
                </div>
                <div>
                  <div className="font-heading font-bold text-3xl sm:text-4xl text-aspire-red">8+</div>
                  <div className="text-sm text-gray-500">Years Excellence</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-[500px] h-[300px] bg-aspire-red"></div>
              <img 
                src="/leaders.png" 
                alt="Students in classroom"
                className="relative z-10 w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-spacing bg-white">
        <div className="container-main">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="border-l-4 border-aspire-red pl-4 sm:pl-8">
              <h2 className="font-heading font-bold text-2xl mb-4">Our Mission</h2>
              <p className="text-gray-600 leading-relaxed">
                To provide exceptional education that empowers students to excel in Cambridge 
                and Edexcel examinations, fostering critical thinking, academic discipline, 
                and a lifelong passion for learning.
              </p>
            </div>
            <div className="border-l-4 border-charcoal pl-4 sm:pl-8">
              <h2 className="font-heading font-bold text-2xl mb-4">Our Vision</h2>
              <p className="text-gray-600 leading-relaxed">
                To be the leading institution for international education in Sri Lanka, 
                recognized for producing graduates who excel academically and contribute 
                meaningfully to society.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="section-spacing bg-surface">
        <div className="container-main">
          <div className="text-center mb-12">
            <span className="label-uppercase text-aspire-red">Our Values</span>
            <h2 className="heading-section mt-2">What Drives Us</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white p-4 sm:p-8 shadow-sm hover:shadow-md transition-all">
                <div className="w-14 h-14 bg-aspire-red/10 flex items-center justify-center mb-6">
                  <value.icon className="w-7 h-7 text-aspire-red" />
                </div>
                <h3 className="font-heading font-bold text-lg mb-3">{value.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Teaching Philosophy */}
      <section className="section-spacing bg-charcoal">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="relative">
              <div className="absolute -bottom-4 -right-4 w-full h-full"></div>
              <img 
                src="/operational.png" 
                alt="Teaching methodology"
                className="relative z-10 w-full h-[400px] object-cover"
              />
            </div>
            <div>
              <span className="label-uppercase text-amber-500">Teaching Philosophy</span>
              <h2 className="heading-section mt-2 mb-6 text-white">Structured Approach to Excellence</h2>
              <p className="text-gray-300 leading-relaxed mb-8">
                Our teaching methodology combines traditional academic rigor with modern 
                pedagogical techniques. We believe in building strong fundamentals, 
                developing critical thinking, and preparing students thoroughly for examinations.
              </p>
              <ul className="space-y-4">
                {[
                  'Comprehensive syllabus coverage',
                  'Regular assessments and feedback',
                  'Past paper practice and analysis',
                  'One-on-one doubt clearing sessions',
                  'Exam technique workshops'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-white">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline / Milestones */}
      <section className="section-spacing bg-white">
        <div className="container-main">
          <div className="text-center mb-12">
            <span className="label-uppercase text-aspire-red">Our Journey</span>
            <h2 className="heading-section mt-2">Years of Excellence</h2>
          </div>

          <div className="max-w-3xl mx-auto">
            {milestones.map((milestone, index) => (
              <div key={index} className="flex gap-6 mb-8 last:mb-0">
                <div className="flex-shrink-0 w-20">
                  <div className="font-mono font-bold text-lg text-aspire-red">{milestone.year}</div>
                </div>
                <div className="flex-grow border-l-2 border-gray-200 pl-6 pb-8">
                  <h3 className="font-heading font-bold text-lg mb-2">{milestone.title}</h3>
                  <p className="text-gray-600 text-sm">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-aspire-red">
        <div className="container-main text-center">
          <h2 className="font-heading font-bold text-3xl text-white mb-6">
            Ready to Join Aspire Academy?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Take the first step towards academic excellence. Register now or contact us for more information.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/register" 
              className="bg-white text-aspire-red px-8 py-4 font-bold uppercase tracking-widest hover:bg-gray-100 transition-colors"
              data-testid="about-register-btn"
            >
              Register Now
            </Link>
            <Link 
              to="/contact" 
              className="border-2 border-white text-white px-8 py-4 font-bold uppercase tracking-widest hover:bg-white hover:text-aspire-red transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
